import React, { useState, useEffect, useRef } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { FitToScreen, Printer, ZoomIn, ZoomOut } from '@carbon/react/icons';
import styles from '../allocation-manager.module.scss';
import UTIF from 'utif';
import {
  calculateDisplayDimensions as calcDisplayDimensions,
  getFitToWidthZoom,
  parseZoomFromInput,
  printImageInNewWindow,
  openDocumentInNewTab,
} from '../utils';

const DEFAULT_ZOOM = 1;

interface TiffViewerProps {
  documentUrl: string;
  fileName: string;
  onFullScreenClick?: () => void;
  fullPage?: boolean;
}

const TiffViewer: React.FC<TiffViewerProps> = ({
  documentUrl,
  fileName,
  fullPage = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tiffData, setTiffData] = useState<{
    width: number;
    height: number;
    displayWidth: number;
    displayHeight: number;
    imageData: ImageData;
    fitScale: number;
  } | null>(null);
  const [showFallbackImage, setShowFallbackImage] = useState(false);
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 10;

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [isFitToWidth, setIsFitToWidth] = useState(false);
  const [zoomInputValue, setZoomInputValue] = useState('');
  const [isEditingZoomInput, setIsEditingZoomInput] = useState(false);
  const [isPanning] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTiff = async () => {
      if (!isMounted) return;

      setIsLoading(true);
      setError(null);
      setTiffData(null);
      setShowFallbackImage(true);
      setZoom(DEFAULT_ZOOM);
      setIsFitToWidth(false);

      try {
        const response = await fetch(documentUrl);
        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();

        if (arrayBuffer.byteLength === 0) {
          throw new Error('Received empty file');
        }

        const ifds = UTIF.decode(arrayBuffer);

        if (ifds.length === 0) {
          throw new Error('No images found in TIFF file');
        }

        const firstPage = ifds[0] as any;

        const width = firstPage.t256 ? (firstPage as any).t256[0] : null;
        const height = firstPage.t257 ? (firstPage as any).t257[0] : null;

        if (!width || !height) {
          throw new Error('Could not determine image dimensions');
        }

        UTIF.decodeImage(arrayBuffer, firstPage);

        const rgba = UTIF.toRGBA8(firstPage);

        const MIN_CONTAINER_SIZE = 100;
        const rawW = containerRef.current?.clientWidth ?? 0;
        const rawH = containerRef.current?.clientHeight ?? 0;
        const containerWidth =
          rawW >= MIN_CONTAINER_SIZE ? rawW : window.innerWidth;
        const containerHeight =
          rawH >= MIN_CONTAINER_SIZE ? rawH : window.innerHeight;

        const scaleW = containerWidth / width;
        const scaleH = containerHeight / height;
        const fitScaleAtDefaultZoom = Math.min(
          scaleW / DEFAULT_ZOOM,
          scaleH / DEFAULT_ZOOM,
          DEFAULT_ZOOM
        );
        const fitScale =
          fitScaleAtDefaultZoom > 0 ? fitScaleAtDefaultZoom : DEFAULT_ZOOM;

        const displayWidth = Math.floor(width * fitScale);
        const displayHeight = Math.floor(height * fitScale);

        const imgData = new ImageData(
          new Uint8ClampedArray(rgba.buffer as ArrayBuffer),
          width,
          height
        );

        if (isMounted) {
          setTiffData({
            width,
            height,
            displayWidth,
            displayHeight,
            imageData: imgData,
            fitScale,
          });
          setShowFallbackImage(false);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : 'Failed to load TIFF file'
          );
          setIsLoading(false);
        }
      }
    };

    loadTiff();

    return () => {
      isMounted = false;
    };
  }, [documentUrl, fileName]);

  useEffect(() => {
    if (!tiffData || !canvasRef.current) return;
    const { width, height, imageData } = tiffData;
    const draw = () => {
      const c = canvasRef.current;
      if (!c || c.width !== width || c.height !== height) return;
      const ctx = c.getContext('2d');
      if (ctx) ctx.putImageData(imageData, 0, 0);
    };

    const rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [tiffData]);

  const displayDimensions = calcDisplayDimensions(tiffData, zoom);

  const handleFitToScreenToggle = () => {
    if (!tiffData || !containerRef.current) return;
    const container = containerRef.current;

    if (isFitToWidth) {
      setZoom(DEFAULT_ZOOM);
      setIsFitToWidth(false);
      setIsEditingZoomInput(false);
      setZoomInputValue('');
    } else {
      const fitZoom = getFitToWidthZoom(
        tiffData.width,
        tiffData.fitScale,
        container.clientWidth
      );
      setZoom(fitZoom);
      setIsFitToWidth(true);
      setIsEditingZoomInput(false);
      setZoomInputValue('');
    }
    requestAnimationFrame(() => {
      container.scrollTop = 0;
    });
  };

  const applyZoomFromInput = () => {
    const newZoom = parseZoomFromInput(
      zoomInputValue,
      zoom,
      isEditingZoomInput
    );
    if (newZoom !== null) {
      setZoom(newZoom);
      setIsFitToWidth(false);
    }
    setIsEditingZoomInput(false);
    setZoomInputValue('');
  };

  const handlePrint = () => {
    if (!tiffData) return;
    const imgDataUrl = canvasRef.current?.toDataURL('image/png');
    if (!imgDataUrl) return;
    printImageInNewWindow(fileName, imgDataUrl);
  };

  const handleDownloadDocument = () => {
    openDocumentInNewTab(documentUrl);
  };

  return (
    <div
      className={`${styles.tiffViewerContainer} ${
        fullPage ? styles.tiffViewerFullPage : ''
      }`.trim()}
      ref={printContainerRef}
    >
      <div className={styles.tiffViewerContent}>
        {error && (
          <div className={styles.viewSourceDocumentBody}>
            <div className={styles.unsupportedFormatPrompt}>
              <div className={styles.unsupportedFormatPromptText}>{error}</div>
              <Button kind="primary" size="md" onClick={handleDownloadDocument}>
                Download
              </Button>
            </div>
          </div>
        )}

        {tiffData && (
          <div className={styles.tiffViewerToolbar}>
            <div className={styles.tiffViewerToolbarZoom}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={(props) => <ZoomOut size={32} {...props} />}
                iconDescription={undefined}
                onClick={() => {
                  const newZoom = Math.max(MIN_ZOOM, zoom - 0.1);
                  setZoom(newZoom);
                  setIsFitToWidth(false);
                  setIsEditingZoomInput(false);
                  setZoomInputValue('');
                }}
              />
              <span className={styles.zoomInputWrapper}>
                <input
                  type="text"
                  className={styles.zoomPercentageInput}
                  value={
                    isEditingZoomInput
                      ? zoomInputValue
                      : String(Math.round(zoom * 100))
                  }
                  onChange={(e) => {
                    setIsEditingZoomInput(true);
                    setZoomInputValue(e.target.value);
                  }}
                  onFocus={() => {
                    setIsEditingZoomInput(true);
                    setZoomInputValue(String(Math.round(zoom * 100)));
                  }}
                  onBlur={() => {
                    applyZoomFromInput();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  aria-label="Zoom percentage"
                />
                <span className={styles.zoomPercentSign}>%</span>
              </span>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={(props) => <ZoomIn size={32} {...props} />}
                iconDescription=""
                onClick={() => {
                  setZoom(Math.min(MAX_ZOOM, zoom + 0.1));
                  setIsFitToWidth(false);
                  setIsEditingZoomInput(false);
                  setZoomInputValue('');
                }}
              />
            </div>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={FitToScreen}
              iconDescription=""
              isSelected={isFitToWidth}
              onClick={handleFitToScreenToggle}
            />
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Printer}
              iconDescription=""
              onClick={handlePrint}
            />
          </div>
        )}

        <div
          className={styles.tiffCanvasContainer}
          ref={containerRef}
          style={{ cursor: 'default' }}
        >
          {isLoading && (
            <div className={styles.tiffViewerLoadingOverlay}>
              <div className={styles.tiffViewerLoadingOverlayCenter}>
                <InlineLoading
                  description="Loading document..."
                  status="active"
                />
              </div>
            </div>
          )}
          {showFallbackImage && (
            <img
              src={documentUrl}
              alt={fileName}
              className={styles.tiffCanvas}
              style={{ maxWidth: '100%', height: 'auto' }}
              onLoad={() => setIsLoading(false)}
            />
          )}

          {tiffData && (
            <div className={styles.tiffCanvasContainerInner}>
              <div className={styles.tiffCanvasWrapper}>
                <canvas
                  key="tiff-canvas"
                  ref={canvasRef}
                  width={tiffData.width}
                  height={tiffData.height}
                  style={{
                    width: `${displayDimensions.width}px`,
                    height: `${displayDimensions.height}px`,
                    display: 'block',
                    transform: 'none',
                    transition: isPanning ? 'none' : 'transform 0.1s ease-out',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TiffViewer;
